import React from 'react';
import { Loader } from '../Loader';
import {
  formatDelta,
  formatNumber,
  getSentimentClass,
  isDeltaDisplayable,
} from './analyticsUtils';

// KPI cards for GET /v1/analytics/summary.
//
// Three rules are enforced here and must not be relaxed:
//  1. Colour comes from the server supplied `sentiment`, never from the sign of
//     the delta. Corrupted devices rising is bad even though the number went up.
//  2. When `deltaReliable` is false no delta figure is rendered at all - the
//     two dates being differenced are not consecutive present days, so any
//     movement shown would be an artefact of a missing nightly rollup.
//  3. A failed request is never dressed up as an empty result. "No figures yet"
//     and "the request failed" are different facts and get different copy.
function AnalyticsSummaryCards({ cards = [], loading = false, error = null, asOfDate }) {
  if (loading) {
    return (
      <div className="analytics-kpi-loader" data-testid="analytics-summary-loading">
        <Loader width="48" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="analytics-error" role="alert" data-testid="analytics-summary-error">
        Summary figures could not be loaded: {error}
      </p>
    );
  }

  if (!cards || cards.length === 0) {
    return (
      <p className="analytics-empty" data-testid="analytics-summary-empty">
        No summary figures are available yet.
      </p>
    );
  }

  return (
    <div className="analytics-kpi-section">
      {asOfDate && (
        <p className="analytics-as-of" data-testid="analytics-as-of">
          As of {asOfDate}
        </p>
      )}
      <div className="analytics-kpi-row">
        {cards.map((card) => {
          const showDelta = isDeltaDisplayable(card);
          return (
            <div
              className="analytics-kpi-card"
              key={card.key}
              data-testid={`analytics-card-${card.key}`}
            >
              <p className="analytics-kpi-label">{card.label}</p>
              <p className="analytics-kpi-value">{formatNumber(card.value)}</p>
              {showDelta ? (
                <p
                  className={`analytics-kpi-delta ${getSentimentClass(card.sentiment)}`}
                  data-testid={`analytics-card-delta-${card.key}`}
                >
                  {formatDelta(card)}
                </p>
              ) : (
                <p
                  className="analytics-kpi-delta analytics-kpi-delta-unreliable"
                  data-testid={`analytics-card-incomplete-${card.key}`}
                >
                  Incomplete data - no comparison available
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AnalyticsSummaryCards;
